/**
 * Startup session restore.
 *
 * On mount: hydrate any persisted token into memory, then validate it with a
 * single `/auth/me` call before trusting it:
 *  - valid   → mark authenticated + seed the current-user cache (no flash of login).
 *  - 401     → token expired/invalid → clear it and fall back to anonymous/mock.
 *  - offline → API unreachable (NetworkError): keep the token and trust it
 *              optimistically, so a temporary backend outage doesn't log the user
 *              out. A later real 401 (handled globally) will still end the session.
 *
 * Returns `restoring` so the shell can show a brief splash instead of flashing the
 * logged-out (mock) state before the check completes.
 */

import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { hydrateTokens, clearTokens } from "@/lib/auth-token";
import { queryKeys } from "@/lib/query-client";
import { ApiException } from "@/lib/api-errors";
import { authApi, toCurrentUser } from "./auth.api";
import { useAuthStore } from "./auth.store";

export function useSessionRestore(): boolean {
  const [restoring, setRestoring] = useState(true);
  const qc = useQueryClient();
  const setAuthenticated = useAuthStore((s) => s.setAuthenticated);

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const restored = await hydrateTokens();
        if (!restored || !active) return;
        try {
          const payload = await authApi.me();
          if (!active) return;
          setAuthenticated();
          qc.setQueryData(queryKeys.currentUser, toCurrentUser(payload));
        } catch (err) {
          if (!active) return;
          if (err instanceof ApiException && err.status === 401) {
            clearTokens();
          } else {
            // Unreachable API: keep the token, trust the session optimistically.
            setAuthenticated();
          }
        }
      } finally {
        if (active) setRestoring(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [qc, setAuthenticated]);

  return restoring;
}
